import React from "react";
import moment from "moment/moment";
import axiosInstance from "../../../../axios";
import { useNavigate } from "react-router-dom";
import { Trash2, Edit3, Calendar, BookOpen, ArrowRight, Check } from "lucide-react";

const AnnouncementCard = ({
  element,
  deleteModal,
  setDeleteModal,
  accouncementActionHandler,
  fetchAnnouncements,
  isStudent,
  user,
  isSelected,
  onSelectToggle,
}) => {
  const navigate = useNavigate();
  const { REACT_APP_LOGIN_API_BASEURL } = process.env;

  const handleDelete = async (id) => {
    const isAuthorOrAdmin = user?.role === 1 || user?.role === "1" || element.createdBy === user?.id || element.createdBy === user?.id?.toString();

    if (isAuthorOrAdmin) {
      try {
        const res = await axiosInstance.delete(`${REACT_APP_LOGIN_API_BASEURL}/announcement/${id}`);
        if (!res.data.error) {
          closeModalAndRefresh();
          return;
        }
      } catch (error) {
        console.error("Error deleting announcement globally:", error);
      }
    }

    // Local delete fallback for recipients
    const deletedIds = JSON.parse(localStorage.getItem("deleted_announcements") || "[]");
    if (!deletedIds.includes(id)) {
      deletedIds.push(id);
      localStorage.setItem("deleted_announcements", JSON.stringify(deletedIds));
    }
    closeModalAndRefresh();
  };

  const closeModalAndRefresh = () => {
    setDeleteModal({
      ...deleteModal,
      modal: false,
      id: null,
      index: null,
    });
    fetchAnnouncements();
    window.dispatchEvent(new Event("update_announcements_count"));
  };

  const handleGoToCourse = () => {
    if (!element?.courseId) return;
    const slug = element.courseName
      ? element.courseName.toString().toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^\w\-]+/g, "")
        .replace(/\-\-+/g, "-")
        .replace(/^-+/, "")
        .replace(/-+$/, "")
      : "course";

    navigate(`/${slug}`, { state: { course: { ID: element.courseId, CourseName: element.courseName } } });
  };

  return (
    <div className={`bg-white rounded-3xl border transition-all duration-300 shadow-sm hover:shadow-md hover:border-slate-350 relative overflow-hidden group ${
      isSelected ? "border-blue-200 ring-4 ring-blue-500/5 shadow-xl shadow-blue-900/5" : "border-slate-200"
    }`}>
      {/* Visual Accent Bar */}
      <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-[#1e3a6e] to-[#264b82]" />

      {/* Delete Confirmation Overlay */}
      {deleteModal.modal && deleteModal.index === element?.id ? (
        <div className="p-6 md:p-8 text-center bg-rose-50/50 backdrop-blur-sm relative z-20">
          <div className="w-12 h-12 bg-rose-100 border border-rose-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Trash2 className="w-6 h-6 text-rose-655 text-rose-600" />
          </div>
          <h4 className="text-lg font-black text-slate-800 mb-1">Delete Announcement?</h4>
          <p className="text-slate-500 text-sm mb-6 max-w-xs mx-auto">Are you sure you want to delete this announcement? This action cannot be undone.</p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() =>
                setDeleteModal({
                  ...deleteModal,
                  modal: false,
                })
              }
              className="px-5 py-2.5 bg-white border border-slate-250 text-slate-655 text-slate-500 hover:text-slate-700 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-slate-100 active:scale-95 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={() => handleDelete(deleteModal.id)}
              className="px-5 py-2.5 bg-rose-600 text-white rounded-xl font-black text-xs uppercase tracking-wider hover:bg-rose-700 shadow-lg shadow-rose-650/20 active:scale-95 transition-all"
            >
              Delete
            </button>
          </div>
        </div>
      ) : (
        <div className="p-6 md:p-7 flex items-start gap-4">
          {/* Checkbox */}
          <label className="relative mt-1 cursor-pointer flex-shrink-0">
            <input
              type="checkbox"
              className="peer sr-only"
              checked={isSelected || false}
              onChange={(e) => onSelectToggle(e.target.checked)}
            />
            <div className="w-5.5 h-5.5 border-2 border-slate-250 rounded-lg bg-white peer-checked:bg-[#264b82] peer-checked:border-[#264b82] transition-all flex items-center justify-center shadow-sm">
              {isSelected && <Check size={11} className="text-white" strokeWidth={4} />}
            </div>
          </label>

          {/* Main card info */}
          <div className="flex-1 min-w-0">
            {/* Header info */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-4 pb-3 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#264b82]/10 border border-[#264b82]/20 flex items-center justify-center font-black text-[#264b82] text-sm uppercase">
                  {(element?.teacher || "T")[0]}
                </div>
                <div>
                  <h4 className="font-black text-slate-800 text-sm leading-tight">
                    {element?.teacher || "Instructor / Admin"}
                  </h4>
                  <p className="text-slate-400 text-xs font-semibold mt-0.5 flex items-center gap-1.5">
                    <Calendar size={11} /> {moment(element?.createdAt).format("dddd, MMM DD, YYYY")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <span className="bg-blue-50 text-[#264b82] text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full border border-blue-100">
                  Announcement
                </span>
                
                {/* Edit and delete controls for creator/teacher */}
                {!isStudent && (user?.role === 1 || user?.role === "1" || element.createdBy === user?.id || element.createdBy === user?.id?.toString()) && (
                  <div className="flex gap-1 ml-1 opacity-60 hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => accouncementActionHandler(element?.description, element)}
                      title="Edit Announcement"
                      className="p-2 hover:bg-amber-50 text-amber-600 rounded-xl transition-colors border border-transparent hover:border-amber-250"
                    >
                      <Edit3 size={13} />
                    </button>
                    <button
                      onClick={() =>
                        setDeleteModal({
                          ...deleteModal,
                          modal: true,
                          id: element.id,
                          index: element.id,
                        })
                      }
                      title="Delete Announcement"
                      className="p-2 hover:bg-rose-50 text-rose-600 rounded-xl transition-colors border border-transparent hover:border-rose-250"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Title / Description */}
            <div className="space-y-2 mb-4">
              <h3 className="text-base font-black text-slate-800 leading-snug">Attention Students!</h3>
              <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap font-medium">{element?.description}</p>
            </div>

            {/* Course Link Section */}
            {element?.courseId && (
              <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 group/course mt-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center shadow-sm text-[#264b82]">
                    <BookOpen size={16} />
                  </div>
                  <div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Linked Course</span>
                    <h5 className="font-extrabold text-slate-800 text-sm leading-none mt-1 group-hover/course:text-[#264b82] transition-colors">
                      {element.courseName || "View Course Details"}
                    </h5>
                  </div>
                </div>
                <button
                  onClick={handleGoToCourse}
                  className="flex items-center justify-center gap-1.5 bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-200 text-slate-700 hover:text-[#264b82] px-4.5 py-2.5 rounded-xl font-bold text-xs transition-all active:scale-95 shadow-sm flex-shrink-0"
                >
                  Go to Course Page <ArrowRight size={13} className="transition-transform group-hover/course:translate-x-0.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementCard;
